import { type ChangeEvent, type ReactNode, useState } from 'react';
import { Checkbox, CheckboxGroup } from '@finn-no/troika-react-checkbox';
import classNames from 'classnames';

import type { CheckboxMap, Facet } from '@/interfaces';

export interface CheckboxStateProps {
    id: string;
    label: string;
    expandCollapseLabel?: string;
    facetList: readonly Facet[];
    displayValues?: Record<string, string | ReactNode>;
    checkboxValues: CheckboxMap;
    hideLabel?: boolean;
    alwaysExpanded?: boolean;
    collapsedSize?: number;
    horizontal?: boolean;
}

export interface CheckboxDispatchProps {
    checkBoxClick: (checkBoxValue: string | Record<string, unknown>) => void;
    triggerFetch?: () => void;
}

export type CheckboxFilterProps = CheckboxDispatchProps & CheckboxStateProps;

export const CheckboxFilter = ({
    id,
    label,
    expandCollapseLabel,
    facetList,
    displayValues,
    checkboxValues = {},
    checkBoxClick,
    triggerFetch,
    hideLabel = false,
    alwaysExpanded = false,
    collapsedSize = 3,
    horizontal = false,
}: CheckboxFilterProps) => {
    const [expanded, setExpanded] = useState(alwaysExpanded);
    const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
        checkBoxClick(event.target.value);
        triggerFetch?.();
    };

    const isDev = process.env.NODE_ENV === 'development';

    const createDisplayValue = (facet: Facet): string | ReactNode => (
        <div className="flex flex-row flex-align-center flex-justify-start">
            {facet.displayValue ?? displayValues[facet.value]}
            {/* biome-ignore lint/suspicious/noGlobalIsNan: This existed before biome was added */}
            {!isNaN(facet.count) && (
                <span className={classNames('u-caption u-ml4', { 'u-mb2': !facet.displayValue })}>({facet.count})</span>
            )}
        </div>
    );

    const displayedFacetList = expanded ? facetList : facetList.slice(0, Math.min(collapsedSize, facetList.length));
    const moreLessLabel = expandCollapseLabel ?? label;

    return (
        <div
            id={id}
            data-testid={id}
            className={classNames('u-mv8 checkbox-filter', {
                'checkbox-filter__hide-label': hideLabel,
                'checkbox-filter__horizontal': horizontal,
            })}>
            <CheckboxGroup data-testid={`CheckboxFilterGroup-${id}`} label={label} value={Object.keys(checkboxValues)} onChange={onChange}>
                {displayedFacetList
                    .filter((facet) => facet.displayValue || (Boolean(displayValues) && displayValues[facet.value]) || isDev)
                    .map((facet) => (
                        <Checkbox
                            data-testid={`CheckboxFilter-${id}-${facet.value}`}
                            key={facet.value}
                            label={createDisplayValue(facet)}
                            value={facet.value}
                            disabled={facet.disabled}
                            name={id}
                        />
                    ))}
            </CheckboxGroup>
            {facetList.length > collapsedSize && !alwaysExpanded && (
                <button
                    type="button"
                    data-testid={`CheckboxFilter-${id}-toggle`}
                    className="button button--link u-mt16"
                    onClick={(): void => setExpanded(!expanded)}>
                    {expanded ? `Færre ${moreLessLabel.toLocaleLowerCase()}` : `Flere ${moreLessLabel.toLocaleLowerCase()}...`}
                </button>
            )}
        </div>
    );
};
